import React from "react";
import Icon from "../../../components/AppIcon";

const HistoricalComparison = ({ weekData, monthData, loading }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })?.format(value || 0);
  };

  const getChangeDisplay = (change, changePercent, hasPreviousData) => {
    // If no previous data exists, show as new data
    if (!hasPreviousData) {
      return {
        value: null,
        trend: "neutral",
        isNew: true,
      };
    }

    // Handle edge cases
    if (change === 0 || !changePercent || !isFinite(changePercent)) {
      return {
        value: 0,
        trend: "neutral",
        isNew: false,
      };
    }

    return {
      value: Math.abs(changePercent).toFixed(1),
      trend: change > 0 ? "up" : "down",
      isNew: false,
    };
  };

  const weekChange = getChangeDisplay(
    weekData?.change,
    weekData?.changePercent,
    (weekData?.previous || 0) > 0
  );

  const monthChange = getChangeDisplay(
    monthData?.change,
    monthData?.changePercent,
    (monthData?.previous || 0) > 0
  );

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border p-4 md:p-6">
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div
            className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: "rgba(30, 58, 95, 0.1)" }}
          >
            <Icon
              name="TrendingUp"
              size={20}
              color="var(--color-primary)"
              className="md:w-6 md:h-6"
            />
          </div>
          <div>
            <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
              Historical Comparison
            </h3>
            <p className="text-xs md:text-sm font-caption text-muted-foreground">
              TRONC performance trends
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="spinner" />
          <span className="ml-3 text-muted-foreground">
            Loading historical data...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div
          className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: "rgba(30, 58, 95, 0.1)" }}
        >
          <Icon
            name="TrendingUp"
            size={20}
            color="var(--color-primary)"
            className="md:w-6 md:h-6"
          />
        </div>
        <div>
          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            Historical Comparison
          </h3>
          <p className="text-xs md:text-sm font-caption text-muted-foreground">
            TRONC performance trends
          </p>
        </div>
      </div>
      <div className="space-y-4 md:space-y-6">
        {/* This Week */}
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs md:text-sm font-caption text-muted-foreground mb-1">
                This Week
              </p>
              <p className="text-xl md:text-2xl lg:text-3xl font-data font-semibold text-foreground">
                {formatCurrency(weekData?.current)}
              </p>
            </div>
            <div className="text-right">
              {weekChange?.value !== null ? (
                <>
                  <div className="flex items-center gap-2 justify-end mb-1">
                    <Icon
                      name={
                        weekChange?.trend === "up"
                          ? "ArrowUp"
                          : weekChange?.trend === "down"
                            ? "ArrowDown"
                            : "Minus"
                      }
                      size={16}
                      color={
                        weekChange?.trend === "up"
                          ? "var(--color-success)"
                          : weekChange?.trend === "down"
                            ? "var(--color-error)"
                            : "var(--color-muted-foreground)"
                      }
                    />
                    <span
                      className="text-sm md:text-base font-data font-semibold"
                      style={{
                        color:
                          weekChange?.trend === "up"
                            ? "var(--color-success)"
                            : weekChange?.trend === "down"
                              ? "var(--color-error)"
                              : "var(--color-muted-foreground)",
                      }}
                    >
                      {weekChange?.value}%
                    </span>
                  </div>
                  <p className="text-xs font-caption text-muted-foreground">
                    vs last week
                  </p>
                </>
              ) : (
                <div>
                  <div className="flex items-center gap-2 justify-end mb-1">
                    <Icon
                      name="Minus"
                      size={16}
                      color="var(--color-muted-foreground)"
                    />
                    <span className="text-sm md:text-base font-data font-semibold text-muted-foreground">
                      {weekChange?.isNew ? "New" : "N/A"}
                    </span>
                  </div>
                  <p className="text-xs font-caption text-muted-foreground">
                    {weekChange?.isNew ? "No previous data" : "vs last week"}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs md:text-sm font-caption text-muted-foreground">
            <Icon name="Calendar" size={14} />
            <span>
              Previous week: {formatCurrency(weekData?.previous)}
            </span>
          </div>
        </div>

        {/* This Month */}
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs md:text-sm font-caption text-muted-foreground mb-1">
                This Month
              </p>
              <p className="text-xl md:text-2xl lg:text-3xl font-data font-semibold text-foreground">
                {formatCurrency(monthData?.current)}
              </p>
            </div>
            <div className="text-right">
              {monthChange?.value !== null ? (
                <>
                  <div className="flex items-center gap-2 justify-end mb-1">
                    <Icon
                      name={
                        monthChange?.trend === "up"
                          ? "ArrowUp"
                          : monthChange?.trend === "down"
                            ? "ArrowDown"
                            : "Minus"
                      }
                      size={16}
                      color={
                        monthChange?.trend === "up"
                          ? "var(--color-success)"
                          : monthChange?.trend === "down"
                            ? "var(--color-error)"
                            : "var(--color-muted-foreground)"
                      }
                    />
                    <span
                      className="text-sm md:text-base font-data font-semibold"
                      style={{
                        color:
                          monthChange?.trend === "up"
                            ? "var(--color-success)"
                            : monthChange?.trend === "down"
                              ? "var(--color-error)"
                              : "var(--color-muted-foreground)",
                      }}
                    >
                      {monthChange?.value}%
                    </span>
                  </div>
                  <p className="text-xs font-caption text-muted-foreground">
                    vs last month
                  </p>
                </>
              ) : (
                <div>
                  <div className="flex items-center gap-2 justify-end mb-1">
                    <Icon
                      name="Minus"
                      size={16}
                      color="var(--color-muted-foreground)"
                    />
                    <span className="text-sm md:text-base font-data font-semibold text-muted-foreground">
                      {monthChange?.isNew ? "New" : "N/A"}
                    </span>
                  </div>
                  <p className="text-xs font-caption text-muted-foreground">
                    {monthChange?.isNew ? "No previous data" : "vs last month"}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs md:text-sm font-caption text-muted-foreground">
            <Icon name="Calendar" size={14} />
            <span>
              Previous month: {formatCurrency(monthData?.previous)}
            </span>
          </div>
        </div>

        {/* Daily Average and Peak */}
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <div className="bg-background rounded-lg p-3 md:p-4 border border-border">
            <p className="text-xs font-caption text-muted-foreground mb-1">
              Daily Average
            </p>
            <p className="text-base md:text-lg font-data font-semibold text-foreground">
              {formatCurrency(weekData?.average)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              This week
            </p>
          </div>
          <div className="bg-background rounded-lg p-3 md:p-4 border border-border">
            <p className="text-xs font-caption text-muted-foreground mb-1">
              Peak Day
            </p>
            <p className="text-base md:text-lg font-data font-semibold text-foreground">
              {formatCurrency(weekData?.peakDay)}
            </p>
            {weekData?.peakDayName && (
              <p className="text-xs text-muted-foreground mt-1">
                {weekData.peakDayName}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoricalComparison;
